/**
 * Utility to generate rich team descriptions based on team data
 */

import type { Team } from '../types'

interface TeamDescription {
  short: string
  full: string
}

// Known team information database
const TEAM_INFO: Record<string, TeamDescription> = {
  // NBA Teams
  'los-angeles-lakers': {
    short: 'Professional basketball team',
    full: 'The Los Angeles Lakers are one of the most successful franchises in NBA history, with 17 championships. Known for their purple and gold colors, the Lakers have been home to legendary players like Magic Johnson, Kareem Abdul-Jabbar, Shaquille O\'Neal, and Kobe Bryant.'
  },
  'golden-state-warriors': {
    short: 'Professional basketball team',
    full: 'The Golden State Warriors are a professional basketball team based in San Francisco. Known for their fast-paced, three-point shooting style, they won championships in 2015, 2017, 2018, and 2022, led by stars like Stephen Curry, Klay Thompson, and Draymond Green.'
  },
  'boston-celtics': {
    short: 'Professional basketball team',
    full: 'The Boston Celtics are one of the most storied franchises in NBA history with 17 championships. Known for their green and white colors and passionate fanbase, the Celtics have a rich tradition of excellence dating back to the Bill Russell era.'
  },
  'chicago-bulls': {
    short: 'Professional basketball team',
    full: 'The Chicago Bulls are best known for their dominance in the 1990s led by Michael Jordan, winning six championships. The team plays at the United Center and is known for their iconic red and black colors.'
  },

  // NFL Teams
  'dallas-cowboys': {
    short: 'Professional football team',
    full: 'The Dallas Cowboys, known as "America\'s Team," are one of the most valuable and popular franchises in the NFL. They have won five Super Bowl championships and play at AT&T Stadium in Arlington, Texas.'
  },
  'new-england-patriots': {
    short: 'Professional football team',
    full: 'The New England Patriots have been one of the most successful NFL franchises, particularly during the Tom Brady and Bill Belichick era, winning six Super Bowl championships between 2001 and 2019.'
  },
  'green-bay-packers': {
    short: 'Professional football team',
    full: 'The Green Bay Packers are the only publicly-owned major professional sports franchise in the United States. Known for their passionate fanbase called "Cheeseheads," they play at the historic Lambeau Field.'
  },
  'pittsburgh-steelers': {
    short: 'Professional football team',
    full: 'The Pittsburgh Steelers are known for their "Steel Curtain" defense and have won six Super Bowl championships. They have a passionate fanbase known as "Steeler Nation" and play at Heinz Field.'
  },

  // MLB Teams
  'new-york-yankees': {
    short: 'Professional baseball team',
    full: 'The New York Yankees are the most successful franchise in MLB history with 27 World Series championships. Known for their pinstripe uniforms and legendary players like Babe Ruth, Lou Gehrig, and Derek Jeter.'
  },
  'los-angeles-dodgers': {
    short: 'Professional baseball team',
    full: 'The Los Angeles Dodgers moved from Brooklyn in 1958 and have won seven World Series championships. They play at Dodger Stadium and are known for their blue and white colors.'
  },
  'boston-red-sox': {
    short: 'Professional baseball team',
    full: 'The Boston Red Sox play at the iconic Fenway Park, home to the famous "Green Monster" left field wall. They have won nine World Series championships and have one of the most passionate fanbases in baseball.'
  },

  // NHL Teams
  'montreal-canadiens': {
    short: 'Professional hockey team',
    full: 'The Montreal Canadiens are the most successful franchise in NHL history with 24 Stanley Cup championships. Known as "Les Habitants" or "Habs," they have a rich French-Canadian heritage and passionate fanbase.'
  },
  'toronto-maple-leafs': {
    short: 'Professional hockey team',
    full: 'The Toronto Maple Leafs are one of the "Original Six" NHL teams and have a passionate fanbase despite not winning a Stanley Cup since 1967. They play at Scotiabank Arena in downtown Toronto.'
  },

  // Soccer Teams
  'ac-milan': {
    short: 'Professional soccer team from Milan',
    full: 'AC Milan is one of the most successful clubs in world football, with seven UEFA Champions League titles and eight FIFA Club World Cup victories. Known for their red and black striped jerseys, they play at San Siro stadium.'
  },
  'fc-barcelona': {
    short: 'Professional soccer team from Barcelona',
    full: 'FC Barcelona is one of the most successful and popular football clubs in the world. Known for their distinctive blue and red colors, they play at Camp Nou and have won numerous La Liga titles and Champions League trophies.'
  },
  'manchester-united': {
    short: 'Professional soccer team from Manchester',
    full: 'Manchester United is one of the most successful clubs in English football history. Known as the "Red Devils," they play at Old Trafford and have won 20 league titles and three European Cups.'
  },
  'real-madrid': {
    short: 'Professional soccer team from Madrid',
    full: 'Real Madrid is the most successful club in UEFA Champions League history with 14 titles. Known as "Los Blancos" for their white jerseys, they play at the Santiago Bernabéu Stadium and are one of the most valuable sports teams in the world.'
  },
  'juventus': {
    short: 'Professional soccer team from Turin',
    full: 'Juventus is the most successful club in Italian football history, with 36 Serie A titles. Known as "The Old Lady" and for their black and white striped jerseys, they play at the Allianz Stadium in Turin.'
  },
  'bayern-munich': {
    short: 'Professional soccer team from Munich',
    full: 'Bayern Munich is the most successful club in German football history, dominating the Bundesliga with over 30 titles. Known for their red jerseys, they play at the Allianz Arena and are one of Europe\'s elite clubs.'
  }
}

/**
 * Generate a team description based on available data
 */
export function generateTeamDescription(team: Team): TeamDescription {
  const teamKey = `${team.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`
  
  // Check if we have specific info for this team
  if (TEAM_INFO[teamKey]) {
    return TEAM_INFO[teamKey]
  }

  // Generate description based on team data
  const sport = team.sport?.toLowerCase() || 'sports'
  const city = team.city || 'their city'
  const league = team.league || 'their league'
  const founded = team.founded ? ` Founded in ${team.founded}` : ''
  
  // Create sport-specific descriptions
  let sportDescription = ''
  let fullDescription = ''
  
  switch (sport) {
    case 'basketball':
      sportDescription = 'Professional basketball team'
      fullDescription = `${team.name} is a professional basketball team based in ${city}, competing in the ${league}.${founded} and known for their competitive spirit and dedicated fanbase.`
      break
    case 'football':
      sportDescription = 'Professional football team'
      fullDescription = `${team.name} is a professional football team representing ${city} in the ${league}.${founded} The team has built a strong tradition and loyal following in their community.`
      break
    case 'baseball':
      sportDescription = 'Professional baseball team'
      fullDescription = `${team.name} is a professional baseball team from ${city}, playing in the ${league}.${founded} They have contributed to the rich tradition of American baseball.`
      break
    case 'hockey':
      sportDescription = 'Professional hockey team'
      fullDescription = `${team.name} is a professional hockey team based in ${city}, competing in the ${league}.${founded} Known for their fast-paced play and passionate fanbase.`
      break
    case 'soccer':
    case 'football': // Handle both American football and soccer
      if (league?.toLowerCase().includes('mls') || league?.toLowerCase().includes('nwsl')) {
        sportDescription = 'Professional soccer team'
        fullDescription = `${team.name} is a professional soccer team representing ${city} in Major League Soccer.${founded} They are part of the growing soccer culture in North America.`
      } else {
        sportDescription = `Professional soccer team from ${city}`
        fullDescription = `${team.name} is a professional soccer club based in ${city}, competing in ${league}.${founded} They represent the passion and tradition of football in their region.`
      }
      break
    default:
      sportDescription = `Professional ${sport} team`
      fullDescription = `${team.name} is a professional ${sport} team based in ${city}, competing in the ${league}.${founded} They are dedicated to excellence in their sport and serving their community.`
  }

  return {
    short: sportDescription,
    full: fullDescription
  }
}

/**
 * Get a short description for team card display
 */
export function getTeamShortDescription(team: Team): string {
  if (team.information && team.information !== `${team.sport} team from ${team.city}`) {
    return team.information
  }
  
  return generateTeamDescription(team).short
}

/**
 * Get a full description for team modal display
 */
export function getTeamFullDescription(team: Team): string {
  if (team.information && team.information !== `${team.sport} team from ${team.city}`) {
    return team.information
  }
  
  return generateTeamDescription(team).full
}